import { useAuth } from "@repo/auth";
import { Button } from "@repo/components";
import { useTranslation } from "@repo/i18n";
import { css } from "@repo/styled-system/css";
import { useMemo, useState } from "react";
import { PageScaffold } from "../../shell/PageScaffold";
import { useVenue } from "../../shell/VenueContext";
import { canAccessVenue } from "../../shell/roles";
import { formatMoney, type ReconciliationRow } from "./depositsApi";
import { useManagerRefund, useReconciliation } from "./queries";

const cell = css({ padding: "2", textAlign: "left", verticalAlign: "top", whiteSpace: "nowrap" });
const table = css({ width: "full", borderCollapse: "collapse", fontSize: "sm" });
const scroll = css({ overflowX: "auto", width: "full" });
const field = css({ display: "flex", flexDirection: "column", gap: "1", fontSize: "sm" });
const controls = css({
  display: "flex",
  gap: "3",
  flexWrap: "wrap",
  alignItems: "flex-end",
  marginBottom: "4",
});

function isoDaysAgo(days: number): string {
  const d = new Date(Date.now() - days * 86_400_000);
  return d.toISOString().slice(0, 10);
}

/**
 * The `/v/:venueId/deposits` reconciliation surface (deposits-and-prepayment.md, R6/R7/R9/R10). Per-venue
 * report over a date range: every row shows the gap-free reference, the collected/refunded/forfeited split
 * with its TVA line and the at-cost provider fee, so every centime reconciles venue-side. A manager can
 * issue a full/partial refund with a mandatory reason (R6); a WAITER never reaches this page (MANAGER-gated
 * route + server re-enforcement). The forfeiture VAT treatment (indemnité vs consideration) is surfaced.
 */
export function DepositReconciliationPage() {
  const { venueId } = useVenue();
  const { claims } = useAuth();
  const { t } = useTranslation();
  const canView = canAccessVenue(claims, venueId, "MANAGER");

  const [from, setFrom] = useState(() => isoDaysAgo(30));
  const [to, setTo] = useState(() => isoDaysAgo(0));

  const recon = useReconciliation(venueId, from, to, canView);

  if (!canView) {
    return (
      <PageScaffold title={t("deposits.title")}>
        <p>{t("deposits.forbidden")}</p>
      </PageScaffold>
    );
  }

  return (
    <PageScaffold title={t("deposits.title")}>
      <p className={css({ color: "fg.muted", marginBottom: "4" })}>{t("deposits.subtitle")}</p>

      <div className={controls}>
        <label className={field}>
          {t("deposits.range.from")}
          <input
            type="date"
            value={from}
            data-testid="deposits-from"
            onChange={(e) => setFrom(e.target.value)}
            className={css({
              border: "1px solid",
              borderColor: "border.default",
              borderRadius: "sm",
              padding: "1",
            })}
          />
        </label>
        <label className={field}>
          {t("deposits.range.to")}
          <input
            type="date"
            value={to}
            data-testid="deposits-to"
            onChange={(e) => setTo(e.target.value)}
            className={css({
              border: "1px solid",
              borderColor: "border.default",
              borderRadius: "sm",
              padding: "1",
            })}
          />
        </label>
      </div>

      {recon.isLoading ? (
        <p>{t("deposits.loading")}</p>
      ) : recon.data && recon.data.rows.length > 0 ? (
        <ReconciliationTable data={recon.data} t={t} />
      ) : (
        <p data-testid="deposits-empty">{t("deposits.empty")}</p>
      )}
    </PageScaffold>
  );
}

function ReconciliationTable({
  data,
  t,
}: {
  data: NonNullable<ReturnType<typeof useReconciliation>["data"]>;
  t: (k: string) => string;
}) {
  return (
    <>
      <div className={scroll}>
        <table className={table} data-testid="deposits-table">
          <thead>
            <tr>
              <th className={cell}>{t("deposits.col.ref")}</th>
              <th className={cell}>{t("deposits.col.status")}</th>
              <th className={cell}>{t("deposits.col.collected")}</th>
              <th className={cell}>{t("deposits.col.collectedTax")}</th>
              <th className={cell}>{t("deposits.col.refunded")}</th>
              <th className={cell}>{t("deposits.col.forfeited")}</th>
              <th className={cell}>{t("deposits.col.fees")}</th>
              <th className={cell}>{t("deposits.col.action")}</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((row) => (
              <ReconRow key={row.paymentRef} row={row} t={t} />
            ))}
          </tbody>
        </table>
      </div>
      <TotalsPanel data={data} t={t} />
    </>
  );
}

function ReconRow({ row, t }: { row: ReconciliationRow; t: (k: string) => string }) {
  const { venueId } = useVenue();
  const refund = useManagerRefund(venueId);
  const refundable =
    row.collected.amountMinor - row.refunded.amountMinor - row.forfeited.amountMinor;
  const currency = row.collected.currency;

  const [open, setOpen] = useState(false);
  const [amountStr, setAmountStr] = useState("");
  const [reason, setReason] = useState("");
  const [failed, setFailed] = useState(false);

  const openModal = () => {
    setAmountStr((refundable / 100).toFixed(2));
    setReason("");
    setFailed(false);
    setOpen(true);
  };

  const amountMinor = Math.round((Number(amountStr) || 0) * 100);
  const amountValid = amountMinor > 0 && amountMinor <= refundable;
  const canSubmit = amountValid && reason.trim() !== "" && !refund.isPending;

  const submit = () => {
    if (!canSubmit) return;
    setFailed(false);
    refund.mutate(
      { bookingRef: row.bookingRef, amountMinor, currency, reason: reason.trim() },
      { onSuccess: () => setOpen(false), onError: () => setFailed(true) },
    );
  };

  return (
    <tr data-testid={`deposits-row-${row.paymentRef}`}>
      <td className={cell}>{row.paymentRef}</td>
      <td className={cell}>{t(`deposits.status.${row.status}`)}</td>
      <td className={cell}>{formatMoney(row.collected)}</td>
      <td className={cell}>{formatMoney(row.collectedTax)}</td>
      <td className={cell}>{formatMoney(row.refunded)}</td>
      <td className={cell}>{formatMoney(row.forfeited)}</td>
      <td className={cell}>{formatMoney(row.feesAtCost)}</td>
      <td className={cell}>
        {open && (
          <RefundModal
            t={t}
            refundableLabel={formatMoney({ amountMinor: refundable, currency })}
            amountStr={amountStr}
            onAmount={setAmountStr}
            reason={reason}
            onReason={setReason}
            amountValid={amountValid}
            canSubmit={canSubmit}
            pending={refund.isPending}
            failed={failed}
            onConfirm={submit}
            onCancel={() => setOpen(false)}
          />
        )}
        {refundable > 0 ? (
          <button
            type="button"
            data-testid={`deposits-refund-${row.paymentRef}`}
            disabled={refund.isPending}
            onClick={openModal}
            className={css({
              display: "inline-flex",
              alignItems: "center",
              minH: "44px",
              px: "2",
              color: "accent.default",
              textDecoration: "underline",
              cursor: "pointer",
            })}
          >
            {t("deposits.refund.action")}
          </button>
        ) : (
          <span className={css({ color: "fg.muted" })}>—</span>
        )}
      </td>
    </tr>
  );
}

/** Refund modal (replaces the old window.prompt): editable amount (defaults to the full refundable,
 *  supports partial), mandatory reason (R6), and success/error feedback. */
function RefundModal(props: {
  t: (k: string) => string;
  refundableLabel: string;
  amountStr: string;
  onAmount: (v: string) => void;
  reason: string;
  onReason: (v: string) => void;
  amountValid: boolean;
  canSubmit: boolean;
  pending: boolean;
  failed: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const { t } = props;
  const input = css({
    px: "2",
    py: "1",
    minH: "40px",
    borderWidth: "1px",
    borderColor: "border.default",
    rounded: "md",
  });
  return (
    <div
      className={css({
        position: "fixed",
        inset: "0",
        bg: "rgba(0,0,0,0.4)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: "50",
        p: "4",
      })}
    >
      <div
        className={css({
          bg: "bg.default",
          rounded: "lg",
          boxShadow: "lg",
          p: "5",
          w: "full",
          maxW: "sm",
          display: "flex",
          flexDirection: "column",
          gap: "3",
        })}
      >
        <h3 className={css({ fontSize: "md", fontWeight: "semibold" })}>
          {t("deposits.refund.title")}
        </h3>
        <p className={css({ fontSize: "sm", color: "fg.muted" })}>
          {t("deposits.refund.refundable")} {props.refundableLabel}
        </p>
        <label
          className={css({ display: "flex", flexDirection: "column", gap: "1", fontSize: "sm" })}
        >
          {t("deposits.refund.amountLabel")}
          <input
            type="number"
            step="0.01"
            min="0"
            value={props.amountStr}
            onChange={(e) => props.onAmount(e.target.value)}
            aria-invalid={!props.amountValid || undefined}
            className={input}
          />
        </label>
        <label
          className={css({ display: "flex", flexDirection: "column", gap: "1", fontSize: "sm" })}
        >
          {t("deposits.refund.reasonLabel")}
          <input
            value={props.reason}
            onChange={(e) => props.onReason(e.target.value)}
            className={input}
          />
        </label>
        {props.failed && (
          <p role="alert" className={css({ fontSize: "sm", color: "fg.error" })}>
            {t("deposits.refund.failed")}
          </p>
        )}
        <div className={css({ display: "flex", gap: "2", justifyContent: "flex-end" })}>
          <Button size="sm" variant="ghost" onClick={props.onCancel}>
            {t("deposits.refund.cancel")}
          </Button>
          <Button
            size="sm"
            disabled={!props.canSubmit}
            loading={props.pending}
            onClick={props.onConfirm}
          >
            {t("deposits.refund.confirm")}
          </Button>
        </div>
      </div>
    </div>
  );
}

function TotalsPanel({
  data,
  t,
}: {
  data: NonNullable<ReturnType<typeof useReconciliation>["data"]>;
  t: (k: string) => string;
}) {
  const totals = useMemo(() => data.totals, [data.totals]);
  const c = data.currency;
  const m = (v: number) => formatMoney({ amountMinor: v, currency: c });
  return (
    <dl
      data-testid="deposits-totals"
      className={css({
        display: "grid",
        gridTemplateColumns: "auto auto",
        gap: "1 4",
        marginTop: "4",
        fontSize: "sm",
        maxWidth: "md",
      })}
    >
      <dt>{t("deposits.totals.collected")}</dt>
      <dd>{m(totals.collectedMinor)}</dd>
      <dt>{t("deposits.totals.collectedTax")}</dt>
      <dd>{m(totals.collectedTaxMinor)}</dd>
      <dt>{t("deposits.totals.refunded")}</dt>
      <dd>{m(totals.refundedMinor)}</dd>
      <dt>{t("deposits.totals.forfeited")}</dt>
      <dd>{m(totals.forfeitedMinor)}</dd>
      <dt>{t("deposits.totals.fees")}</dt>
      <dd>{m(totals.feesAtCostMinor)}</dd>
    </dl>
  );
}
